import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Crown, Zap, AlertTriangle, RefreshCw, Settings as SettingsIcon } from 'lucide-react';

import { Button } from '../../components/ui/button';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../../components/ui/dialog';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import { useAuth } from '../../context/AuthContext';

const API = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/+$/, '');

const PLAN_OPTIONS = [
  { value: 'trial',   label: 'Trial',   requiresCycle: false, icon: Zap },
  { value: 'starter', label: 'Starter', requiresCycle: true,  icon: Zap },
  { value: 'growth',  label: 'Growth',  requiresCycle: true,  icon: Crown },
  { value: 'custom',  label: 'Custom',  requiresCycle: false, icon: SettingsIcon },
];

const fmtDate = (iso) => {
  if (!iso) return '-';
  try {
    return new Date(iso).toLocaleDateString('de-DE', { day: 'numeric', month: 'short', year: 'numeric' });
  } catch { return iso; }
};

const AdminPackages = () => {
  const { getAuthHeader } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('');
  const [editing, setEditing] = useState(null);      // user-row being edited
  const [customEditing, setCustomEditing] = useState(null);

  const loadUsers = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/api/admin/users`, { headers: getAuthHeader() });
      setUsers(res.data?.users || []);
    } catch (e) {
      toast.error('Laden der User fehlgeschlagen');
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [getAuthHeader]);

  useEffect(() => { loadUsers(); }, [loadUsers]);

  const changePlan = async (userId, plan, billing_cycle) => {
    try {
      await axios.post(
        `${API}/api/admin/users/${userId}/subscription`,
        { plan, billing_cycle },
        { headers: getAuthHeader() },
      );
      toast.success(`Plan gewechselt: ${plan}`);
      setEditing(null);
      loadUsers();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Plan-Wechsel fehlgeschlagen');
    }
  };

  const saveCustom = async (userId, config) => {
    try {
      await axios.post(
        `${API}/api/admin/users/${userId}/subscription/custom`,
        config,
        { headers: getAuthHeader() },
      );
      toast.success('Custom-Plan gespeichert');
      setCustomEditing(null);
      loadUsers();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Speichern fehlgeschlagen');
    }
  };

  const forceReset = async (userId, email) => {
    if (!window.confirm(`Credits für ${email} jetzt zurücksetzen?`)) return;
    try {
      await axios.post(
        `${API}/api/admin/users/${userId}/subscription/reset`, {},
        { headers: getAuthHeader() },
      );
      toast.success('Credits zurückgesetzt');
      loadUsers();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Reset fehlgeschlagen');
    }
  };

  const setTrialStart = async (userId, email) => {
    const input = window.prompt(`Trial-Startdatum für ${email} (YYYY-MM-DD):`, new Date().toISOString().slice(0, 10));
    if (!input) return;
    try {
      await axios.post(
        `${API}/api/admin/users/${userId}/trial-start`,
        { start_date: input },
        { headers: getAuthHeader() },
      );
      toast.success(`Trial-Start gesetzt auf ${input}`);
      loadUsers();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Aktion fehlgeschlagen');
    }
  };

  const filtered = users.filter(u => {
    if (!filter.trim()) return true;
    const q = filter.toLowerCase();
    return (u.email || '').toLowerCase().includes(q) || (u.name || '').toLowerCase().includes(q);
  });

  return (
    <div className="p-6 bg-[#0A1628] min-h-full text-white" data-testid="admin-packages-page">
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Crown className="w-6 h-6 text-amber-400" /> Pakete & Subscriptions
          </h1>
          <p className="text-sm text-[#A0AEC0] mt-1">Plan-Verwaltung, Trial-Start und Credit-Resets für alle User.</p>
        </div>
        <Button variant="outline" onClick={loadUsers} className="border-[#1A2B42] text-white">
          <RefreshCw className="w-4 h-4 mr-2" /> Neu laden
        </Button>
      </div>

      <Input
        data-testid="admin-packages-search"
        placeholder="User nach E-Mail oder Name filtern…"
        value={filter}
        onChange={(e) => setFilter(e.target.value)}
        className="mb-4 bg-[#0f1f36] border-[#1A2B42] text-white max-w-md"
      />

      <div className="overflow-auto rounded-lg border border-[#1A2B42] bg-[#0f1f36]">
        <table className="w-full text-sm">
          <thead className="bg-[#0A1628] text-[#A0AEC0] uppercase text-[10px] tracking-wider">
            <tr>
              <th className="text-left px-4 py-2">User</th>
              <th className="text-left px-4 py-2">Plan</th>
              <th className="text-left px-4 py-2">Zyklus</th>
              <th className="text-left px-4 py-2">Credits</th>
              <th className="text-left px-4 py-2">Trial</th>
              <th className="text-left px-4 py-2">Next Reset</th>
              <th className="text-right px-4 py-2">Aktionen</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={7} className="text-center py-10 text-[#5A6C7D]">Laden…</td></tr>
            )}
            {!loading && filtered.length === 0 && (
              <tr><td colSpan={7} className="text-center py-10 text-[#5A6C7D]">Keine User gefunden.</td></tr>
            )}
            {!loading && filtered.map((u) => {
              const s = u.subscription || {};
              const expired = s.trial_expired;
              return (
                <tr key={u.id} className="border-t border-[#1A2B42] hover:bg-[#0A1628]/60">
                  <td className="px-4 py-3">
                    <div className="font-medium">{u.name || u.email}</div>
                    <div className="text-xs text-[#5A6C7D]">{u.email}</div>
                  </td>
                  <td className="px-4 py-3">
                    <span
                      data-testid={`plan-cell-${u.id}`}
                      className={`inline-block px-2 py-0.5 rounded-full text-xs border ${
                        s.plan === 'trial' ? 'border-amber-400/40 text-amber-300 bg-amber-400/10' :
                        s.plan === 'starter' ? 'border-[#00D9C8]/40 text-[#00D9C8] bg-[#00D9C8]/10' :
                        s.plan === 'growth' ? 'border-violet-300/40 text-violet-300 bg-violet-300/10' :
                        s.plan === 'custom' ? 'border-sky-300/40 text-sky-300 bg-sky-300/10' :
                        'border-emerald-300/40 text-emerald-300 bg-emerald-300/10'
                      }`}
                    >
                      {s.plan || '—'}
                    </span>
                    {s.custom && <span className="ml-1 text-[10px] text-sky-300">(custom)</span>}
                  </td>
                  <td className="px-4 py-3 text-xs text-[#A0AEC0]">{s.billing_cycle || '—'}</td>
                  <td className="px-4 py-3 text-xs">{(s.credits_used || 0)} / {s.credits_total || 0}</td>
                  <td className="px-4 py-3 text-xs">
                    {s.trial_start_date || '—'}
                    {expired && (
                      <div className="mt-0.5 inline-flex items-center gap-1 text-red-400 text-[10px]">
                        <AlertTriangle className="w-3 h-3" /> abgelaufen
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-3 text-xs text-[#A0AEC0]">{fmtDate(s.credits_reset_date)}</td>
                  <td className="px-4 py-3">
                    <div className="flex gap-1 justify-end flex-wrap">
                      <Button
                        size="sm" variant="outline"
                        data-testid={`change-plan-btn-${u.id}`}
                        className="border-[#1A2B42] text-white text-xs h-7"
                        onClick={() => setEditing({ ...u, _newPlan: s.plan || 'trial', _newCycle: s.billing_cycle || 'monthly' })}
                      >
                        Plan
                      </Button>
                      <Button
                        size="sm" variant="outline"
                        data-testid={`custom-plan-btn-${u.id}`}
                        className="border-[#1A2B42] text-white text-xs h-7"
                        onClick={() => setCustomEditing({
                          id: u.id,
                          email: u.email,
                          credits_total: s.credits_total || 0,
                          features: s.features || { video: false, press_distribution: false, social_channels: 1, brands: 1 },
                          notes: s.notes || '',
                        })}
                      >
                        Custom
                      </Button>
                      <Button
                        size="sm" variant="outline"
                        className="border-[#1A2B42] text-white text-xs h-7"
                        onClick={() => setTrialStart(u.id, u.email)}
                      >
                        Trial-Start
                      </Button>
                      <Button
                        size="sm" variant="outline"
                        className="border-[#1A2B42] text-white text-xs h-7"
                        onClick={() => forceReset(u.id, u.email)}
                      >
                        Credits Reset
                      </Button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Change Plan Dialog */}
      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="bg-[#0f1f36] border-[#1A2B42] text-white max-w-md" data-testid="change-plan-dialog">
          <DialogHeader>
            <DialogTitle>Plan wechseln — {editing?.email}</DialogTitle>
          </DialogHeader>
          {editing && (
            <div className="space-y-4 py-2">
              <div>
                <Label>Plan</Label>
                <Select value={editing._newPlan} onValueChange={(v) => setEditing({ ...editing, _newPlan: v })}>
                  <SelectTrigger className="bg-[#0A1628] border-[#1A2B42]"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#0f1f36] border-[#1A2B42] text-white">
                    {PLAN_OPTIONS.filter(p => p.value !== 'custom').map(p => (
                      <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {(editing._newPlan === 'starter' || editing._newPlan === 'growth') && (
                <div>
                  <Label>Abrechnungszyklus</Label>
                  <Select value={editing._newCycle} onValueChange={(v) => setEditing({ ...editing, _newCycle: v })}>
                    <SelectTrigger className="bg-[#0A1628] border-[#1A2B42]"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-[#0f1f36] border-[#1A2B42] text-white">
                      <SelectItem value="monthly">Monatlich</SelectItem>
                      <SelectItem value="yearly">Jährlich</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={() => setEditing(null)}>Abbrechen</Button>
            <Button
              data-testid="confirm-plan-change"
              className="bg-[#00D9C8] text-[#0A1628] hover:bg-[#00BFB0]"
              onClick={() => changePlan(
                editing.id,
                editing._newPlan,
                (editing._newPlan === 'starter' || editing._newPlan === 'growth') ? editing._newCycle : null,
              )}
            >
              Speichern
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Custom Config Dialog */}
      <Dialog open={!!customEditing} onOpenChange={(o) => !o && setCustomEditing(null)}>
        <DialogContent className="bg-[#0f1f36] border-[#1A2B42] text-white max-w-md" data-testid="custom-plan-dialog">
          <DialogHeader>
            <DialogTitle>Custom Plan — {customEditing?.email}</DialogTitle>
          </DialogHeader>
          {customEditing && (
            <div className="space-y-3 py-2">
              <div>
                <Label>Credits / Monat</Label>
                <Input
                  type="number"
                  value={customEditing.credits_total}
                  onChange={(e) => setCustomEditing({ ...customEditing, credits_total: parseInt(e.target.value || '0') })}
                  className="bg-[#0A1628] border-[#1A2B42]"
                  data-testid="custom-credits"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Social Channels</Label>
                  <Input
                    type="number"
                    value={customEditing.features.social_channels}
                    onChange={(e) => setCustomEditing({
                      ...customEditing,
                      features: { ...customEditing.features, social_channels: parseInt(e.target.value || '0') },
                    })}
                    className="bg-[#0A1628] border-[#1A2B42]"
                  />
                </div>
                <div>
                  <Label>Brands</Label>
                  <Input
                    type="number"
                    value={customEditing.features.brands}
                    onChange={(e) => setCustomEditing({
                      ...customEditing,
                      features: { ...customEditing.features, brands: parseInt(e.target.value || '0') },
                    })}
                    className="bg-[#0A1628] border-[#1A2B42]"
                  />
                </div>
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="cust-video"
                  checked={!!customEditing.features.video}
                  onChange={(e) => setCustomEditing({
                    ...customEditing,
                    features: { ...customEditing.features, video: e.target.checked },
                  })}
                />
                <Label htmlFor="cust-video" className="cursor-pointer">Video-Generierung</Label>
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="cust-press"
                  checked={!!customEditing.features.press_distribution}
                  onChange={(e) => setCustomEditing({
                    ...customEditing,
                    features: { ...customEditing.features, press_distribution: e.target.checked },
                  })}
                />
                <Label htmlFor="cust-press" className="cursor-pointer">Presseverteiler</Label>
              </div>
              <div>
                <Label>Notizen</Label>
                <Input
                  value={customEditing.notes}
                  onChange={(e) => setCustomEditing({ ...customEditing, notes: e.target.value })}
                  className="bg-[#0A1628] border-[#1A2B42]"
                  placeholder="z.B. Enterprise, Kulanz 3 Monate, Demo-Account…"
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={() => setCustomEditing(null)}>Abbrechen</Button>
            <Button
              data-testid="save-custom-plan"
              className="bg-[#00D9C8] text-[#0A1628] hover:bg-[#00BFB0]"
              onClick={() => saveCustom(customEditing.id, {
                credits_total: customEditing.credits_total,
                features: customEditing.features,
                notes: customEditing.notes,
              })}
            >
              Custom Plan speichern
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminPackages;
